package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.AccessLevel
import com.evoapps.evoshift.domain.model.AccessProblem
import com.evoapps.evoshift.domain.model.AccessRefusedException
import com.evoapps.evoshift.domain.model.CustomRole
import com.evoapps.evoshift.domain.model.Feature
import com.evoapps.evoshift.domain.repository.AccessRepository
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject
import kotlin.coroutines.cancellation.CancellationException

/** SH-270: owners/{ownerId}/roles/{id} as functions/roles.js saveRole writes it. */
@Serializable
data class CustomRoleDto(
    val name: String = "",
    val levels: Map<String, Int> = emptyMap()
)

/**
 * SH-270: reads the business's roles from Firestore (firestore.rules: its owner and the managers
 * of its team); every write is a callable of functions/roles.js.
 */
class AccessRepositoryImpl(
    private val firestore: AppFirestore,
    private val functions: CloudFunctionsClient
) : AccessRepository {

    override suspend fun getRoles(ownerId: String): Result<List<CustomRole>> = guarded {
        firestore.collection("owners").document(ownerId).collection("roles").get().documents.map {
            toDomain(it.id, it.data<CustomRoleDto>())
        }
    }

    override suspend fun saveRole(roleId: String?, name: String, levels: Map<Feature, AccessLevel>): Result<String> = guarded {
        val result = functions.call("saveRole", buildJsonObject {
            if (roleId != null) put("roleId", roleId)
            put("name", name)
            putJsonObject("levels") { levels.forEach { (feature, level) -> put(feature.id, level.value) } }
        })
        (result as? JsonObject)?.get("roleId")?.jsonPrimitive?.contentOrNull ?: roleId.orEmpty()
    }

    override suspend fun deleteRole(roleId: String): Result<Unit> = guarded {
        functions.call("deleteRole", buildJsonObject { put("roleId", roleId) })
        Unit
    }

    override suspend fun setEngagementAccess(
        userId: String,
        workplaceId: String,
        roleId: String,
        overrides: Map<Feature, AccessLevel>
    ): Result<Unit> = guarded {
        functions.call("setEngagementAccess", buildJsonObject {
            put("userId", userId)
            put("workplaceId", workplaceId)
            put("roleId", roleId)
            putJsonObject("overrides") { overrides.forEach { (feature, level) -> put(feature.id, level.value) } }
        })
        Unit
    }

    private inline fun <T> guarded(block: () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        val reason = ((e.details as? JsonObject)?.get("reason"))?.jsonPrimitive?.contentOrNull
        val problem = AccessProblem.entries.firstOrNull { it.name == reason }
        Result.failure(if (problem != null) AccessRefusedException(problem) else e)
    } catch (e: Exception) {
        Result.failure(e)
    }

    companion object {
        /** Unknown features are left out (a newer registry), unknown levels read as none. */
        fun toDomain(id: String, dto: CustomRoleDto): CustomRole = CustomRole(
            id = id,
            name = dto.name,
            levels = Feature.entries.associateWith { AccessLevel.of(dto.levels[it.id]) }
        )
    }
}
